import { Box, Collapse, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MAX_FOLDER_DEPTH } from '../../../../constants/folder';
import { getAPIErrorMessage } from '../../../../helpers/helpers';
import { FolderService } from '../../../../service/FolderService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useSidebarTreeStore } from '../../../../store/storeSidebarTree';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { AnimatedListItem } from '../dnd/AnimatedListItem';
import { DraggableItemShell } from '../dnd/DraggableItemShell';
import { dropHighlightSx } from '../dnd/dropHighlightSx';
import {
  buildFolderDragData,
  getReservedFolderType,
  isLockedFolder,
} from '../dnd/dropRules';
import { getNodeDepth } from '../dnd/folderDepth';
import { buildSidebarChildren } from '../dnd/sidebarChildren';
import type { FolderDropData, ReservedFolderType } from '../dnd/types';
import { useCombinedDndRef } from '../dnd/useCombinedDndRef';
import { useDraggableNode } from '../dnd/useDraggableNode';
import { useDropTargetNode } from '../dnd/useDropTargetNode';
import { useExpandOnHover } from '../dnd/useExpandOnHover';
import { isVirtualFolderId } from '../utils';
import { AgentItem } from './AgentItem';
import { AgentVectorMemoryItem } from './AgentVectorMemoryItem';
import { FluxBotItem } from './FluxBotItem';
import { FolderItemHeader } from './FolderItemHeader';
import { FolderItemMenu } from './FolderItemMenu';
import { useAgentsSidebarContext } from './context';
import { useFolderLocalContent } from './useFolderLocalContent';

interface FolderItemProps {
  folder: Folder;
  customItems?: React.ReactNode[];
  spaceId?: string | null;
  ancestorReservedType?: ReservedFolderType | null;
  onFlowRename?: (flow: Flows, folderId: string) => void;
  onFlowDelete?: (flow: Flows) => void;
  onFlowShare?: (flow: Flows, folderId: string) => void;
  onFlowOpenInNewTab?: (flow: Flows) => void;
  onFlowCreateInterface?: (flow: Flows, folderId: string) => void;
  onFlowAddToLibrary?: (flow: Flows, folderId: string) => void;
  onFlowDuplicate?: (flow: Flows, folderId: string) => void;
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
  onAddSubfolder?: (folder: Folder) => void;
  onAddAgent?: (folder: Folder) => void;
  onObjectEdit?: (obj: import('./context').FluxObject) => void;
  onObjectDelete?: (obj: import('./context').FluxObject) => void;
  onVectorEdit?: (ns: import('../../../../types/namespace').Namespace) => void;
  onVectorDelete?: (
    ns: import('../../../../types/namespace').Namespace,
  ) => void;
}

const FolderItemComponent: React.FC<FolderItemProps> = ({
  folder,
  customItems,
  spaceId = null,
  ancestorReservedType = null,
  onFlowRename,
  onFlowDelete,
  onFlowShare,
  onFlowOpenInNewTab,
  onFlowCreateInterface,
  onFlowAddToLibrary,
  onFlowDuplicate,
  onFolderRename,
  onFolderDelete,
  onAddSubfolder,
  onAddAgent,
  onObjectEdit,
  onObjectDelete,
  onVectorEdit,
  onVectorDelete,
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();
  const setNotification = useNotificationStore(s => s.setNotification);
  const [isOpen, setIsOpen] = useState(false);
  const [localFolder, setLocalFolder] = useFolderLocalContent(folder);
  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const folderHeaderRef = useRef<HTMLDivElement | null>(null);
  const depth = useSidebarTreeStore(s => getNodeDepth(s.nodes, folder.id) || 1);

  const reservedType = getReservedFolderType(folder);
  const isReserved = reservedType !== null;
  const isLocked = isLockedFolder(folder);
  const isVirtual = isVirtualFolderId(folder.id);
  const folderLocked = isLocked || isVirtual;
  const dragDisabled = folderLocked || isReserved;
  const dropDisabled = isLocked || isReserved;

  const canAddSubfolder =
    !isReserved &&
    !isVirtual &&
    !isLocked &&
    !!onAddSubfolder &&
    depth < MAX_FOLDER_DEPTH;
  const hasFolderActions =
    !isReserved &&
    !isVirtual &&
    (onFolderRename || onFolderDelete || canAddSubfolder);
  const showAddAgent = !isLocked && !isVirtual && !!onAddAgent;

  const resolvedSpaceId = spaceId || folder.space || null;

  const dragData = useMemo(
    () => buildFolderDragData(folder, ancestorReservedType, resolvedSpaceId),
    [folder, ancestorReservedType, resolvedSpaceId],
  );
  const draggable = useDraggableNode({
    data: dragData,
    label: folder.name,
    disabled: dragDisabled,
  });
  const dropData: FolderDropData = useMemo(
    () => ({
      kind: 'folder-drop',
      folderId: folder.id,
      parentId: folder.parentId ?? null,
      spaceId: resolvedSpaceId,
      reservedType,
      ancestorReservedType,
      isVirtual,
      isLocked: folderLocked,
    }),
    [
      folder,
      resolvedSpaceId,
      reservedType,
      ancestorReservedType,
      isVirtual,
      folderLocked,
    ],
  );
  const dropTarget = useDropTargetNode({
    data: dropData,
    mode: 'folder',
    disabled: dropDisabled,
  });

  const showDropAbove = dropTarget.position === 'above';
  const showDropBelow = dropTarget.position === 'below';
  const showDropInside = dropTarget.position === 'inside' && dropTarget.isOver;

  const headerRef = useCombinedDndRef(
    folderHeaderRef,
    draggable.setRef,
    dropTarget.setRef,
  );

  useExpandOnHover({
    isOver: dropTarget.isOver,
    isOpen,
    onExpand: useCallback(() => setIsOpen(true), []),
  });

  const children = useMemo(
    () =>
      buildSidebarChildren({
        subfolders: localFolder.folders,
        flows: localFolder.flows,
        objects: localFolder.objects,
        namespaces: localFolder.namespaces,
      }),
    [
      localFolder.folders,
      localFolder.flows,
      localFolder.objects,
      localFolder.namespaces,
    ],
  );

  const hasItems = () => {
    return (customItems?.length ?? 0) > 0 || children.length > 0;
  };

  const handleToggle = async () => {
    if (!isOpen && (!localFolder.flows || localFolder.flows.length === 0)) {
      if (!isVirtual) {
        try {
          const res = await FolderService.getOne(folder.id);
          setLocalFolder(prev => ({
            ...prev,
            ...res.data,
            folders: prev.folders?.length ? prev.folders : res.data.folders,
          }));
        } catch (error) {
          setNotification({
            type: NotificationType.Error,
            message: getAPIErrorMessage(error),
          });
        }
      }
    }
    setIsOpen(!isOpen);
  };

  const handleMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    const header = folderHeaderRef.current;
    if (header) {
      setMenuAnchor(header);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const childAncestor = reservedType ?? ancestorReservedType ?? null;
  const dropHighlight = dropHighlightSx(showDropInside, palette.blue[500]);

  return (
    <DraggableItemShell
      isDragging={draggable.isDragging}
      style={dropHighlight}
      dropAbove={showDropAbove}
      dropBelow={showDropBelow}
    >
      <FolderItemHeader
        folder={folder}
        isOpen={isOpen}
        isReserved={isReserved}
        isHovered={isHovered}
        showAddAgent={showAddAgent}
        showMenuButton={!!hasFolderActions}
        menuOpen={!!menuAnchor}
        headerRef={headerRef}
        onToggle={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onAddAgent={onAddAgent}
        onMenuOpen={handleMenuOpen}
      />
      {menuAnchor && (
        <FolderItemMenu
          folder={folder}
          anchorEl={menuAnchor}
          onClose={handleMenuClose}
          onRename={onFolderRename}
          onDelete={onFolderDelete}
          canAddSubfolder={canAddSubfolder}
          onAddSubfolder={onAddSubfolder}
        />
      )}
      <Collapse in={isOpen}>
        <Box sx={{ marginLeft: '8px' }}>
          {hasItems() ? (
            customItems ? (
              customItems
            ) : (
              <AnimatePresence initial={false}>
                {children.map(child => {
                  if (child.kind === 'folder') {
                    return (
                      <AnimatedListItem
                        key={`folder:${child.id}`}
                        itemKey={child.id}
                      >
                        <FolderItem
                          folder={child.node}
                          spaceId={resolvedSpaceId}
                          ancestorReservedType={childAncestor}
                          onFlowRename={onFlowRename}
                          onFlowDelete={onFlowDelete}
                          onFlowShare={onFlowShare}
                          onFlowOpenInNewTab={onFlowOpenInNewTab}
                          onFlowCreateInterface={onFlowCreateInterface}
                          onFlowAddToLibrary={onFlowAddToLibrary}
                          onFlowDuplicate={onFlowDuplicate}
                          onFolderRename={onFolderRename}
                          onFolderDelete={onFolderDelete}
                          onAddSubfolder={onAddSubfolder}
                          onAddAgent={onAddAgent}
                          onObjectEdit={onObjectEdit}
                          onObjectDelete={onObjectDelete}
                          onVectorEdit={onVectorEdit}
                          onVectorDelete={onVectorDelete}
                        />
                      </AnimatedListItem>
                    );
                  }
                  if (child.kind === 'flow' || child.kind === 'form') {
                    return (
                      <AnimatedListItem
                        key={`flow:${child.id}`}
                        itemKey={child.id}
                      >
                        <AgentItem
                          flow={child.node}
                          folderId={folder.id}
                          flowFolderName={localFolder.name}
                          isNested
                          spaceId={resolvedSpaceId}
                          parentId={folder.id}
                          ancestorReservedType={childAncestor}
                          onRename={onFlowRename}
                          onDelete={onFlowDelete}
                          onShare={onFlowShare}
                          onOpenInNewTab={onFlowOpenInNewTab}
                          onCreateInterface={onFlowCreateInterface}
                          onAddToLibrary={onFlowAddToLibrary}
                          onDuplicate={onFlowDuplicate}
                        />
                      </AnimatedListItem>
                    );
                  }
                  if (child.kind === 'object') {
                    return (
                      <AnimatedListItem
                        key={`object:${child.id}`}
                        itemKey={child.id}
                      >
                        <FluxBotItem
                          object={child.node}
                          spaceId={resolvedSpaceId}
                          parentId={folder.id}
                          ancestorReservedType={childAncestor}
                          onEdit={onObjectEdit}
                          onDelete={onObjectDelete}
                          isNested
                        />
                      </AnimatedListItem>
                    );
                  }
                  if (child.kind === 'vector') {
                    return (
                      <AnimatedListItem
                        key={`ns:${child.id}`}
                        itemKey={child.id}
                      >
                        <AgentVectorMemoryItem
                          namespace={child.node}
                          spaceId={resolvedSpaceId}
                          parentId={folder.id}
                          ancestorReservedType={childAncestor}
                          onEdit={onVectorEdit ?? (() => undefined)}
                          onDelete={onVectorDelete ?? (() => undefined)}
                        />
                      </AnimatedListItem>
                    );
                  }
                  return null;
                })}
              </AnimatePresence>
            )
          ) : (
            <Typography
              sx={{
                color: isDarkMode ? palette.gray[500] : palette.gray[400],
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontStyle: 'italic',
                padding: '8px 16px',
              }}
            >
              No items
            </Typography>
          )}
        </Box>
      </Collapse>
    </DraggableItemShell>
  );
};

export const FolderItem = React.memo(FolderItemComponent);
